import React from 'react'
import { Button, Input, Label, Card, CardContent, Radio } from '@/shared/ui'
import SaveFeedbackModal from '@/shared/ui/SaveFeedbackModal'
import { useToast } from '@/shared/ui/Toast'
import { getStripeKeysEnv, setStripeKeysEnv, setStripeWebhookSecret, hasStripeWebhookSecret, getStripeModes, setStripeModes } from '../services/settingsService'

export default function StripeCredentialsForm() {
  const { add: toast } = useToast()
  const [mode, setMode] = React.useState<'test'|'live'>('test')
  const [modeLocal, setModeLocal] = React.useState<'test'|'live'>('test')
  const [modeSite, setModeSite] = React.useState<'test'|'live'>('live')
  const [pubTest, setPubTest] = React.useState('')
  const [secTest, setSecTest] = React.useState('')
  const [pubLive, setPubLive] = React.useState('')
  const [secLive, setSecLive] = React.useState('')
  const [webhookSecret, setWebhookSecret] = React.useState('')
  const [hasWebhook, setHasWebhook] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const cfg = await getStripeKeysEnv()
        const modes = await getStripeModes().catch(()=> ({} as any))
        const hasWh = await hasStripeWebhookSecret().catch(()=> false)
        if (cancelled) return
        setMode((cfg as any).mode || 'test')
        setModeLocal((modes as any).mode_local || (cfg as any).mode || 'test')
        setModeSite((modes as any).mode_site || (cfg as any).mode || 'live')
        setPubTest(cfg.test.publishable || '')
        setSecTest(cfg.test.secret || '')
        setPubLive(cfg.live.publishable || '')
        setSecLive(cfg.live.secret || '')
        setHasWebhook(!!hasWh)
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Falha ao carregar credenciais do Stripe')
      } finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      await setStripeKeysEnv({
        test: { publishable: pubTest.trim(), secret: secTest.trim() },
        live: { publishable: pubLive.trim(), secret: secLive.trim() },
        // espelho legado usa modo do site
        mode: modeSite,
      })
      await setStripeModes({ mode, mode_local: modeLocal, mode_site: modeSite })
      if (webhookSecret.trim()) {
        await setStripeWebhookSecret(webhookSecret.trim())
        setWebhookSecret('')
        setHasWebhook(true)
      }
      toast({ title: 'Stripe', description: 'Credenciais salvas com sucesso.', variant:'success', soft:true })
      setSaved(true)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao salvar credenciais do Stripe')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <form className="space-y-4 pt-4" onSubmit={onSave} aria-busy={saving || loading}>
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}

          <div className="rounded-md border p-3">
            <div className="mb-2 text-sm font-medium">Modo do Stripe (Localhost)</div>
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 rounded-md border px-2 py-1 ${modeLocal==='test'?'border-primary':'border-border'}`}>
                <Radio name="stripe_mode_local" checked={modeLocal==='test'} onChange={()=> setModeLocal('test')} />
                <span className="text-sm">Teste</span>
              </label>
              <label className={`flex items-center gap-2 rounded-md border px-2 py-1 ${modeLocal==='live'?'border-primary':'border-border'}`}>
                <Radio name="stripe_mode_local" checked={modeLocal==='live'} onChange={()=> setModeLocal('live')} />
                <span className="text-sm">Produção</span>
              </label>
            </div>
          </div>

          <div className="rounded-md border p-3">
            <div className="mb-2 text-sm font-medium">Modo do Stripe (Site oficial)</div>
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 rounded-md border px-2 py-1 ${modeSite==='test'?'border-primary':'border-border'}`}>
                <Radio name="stripe_mode_site" checked={modeSite==='test'} onChange={()=> setModeSite('test')} />
                <span className="text-sm">Teste</span>
              </label>
              <label className={`flex items-center gap-2 rounded-md border px-2 py-1 ${modeSite==='live'?'border-primary':'border-border'}`}>
                <Radio name="stripe_mode_site" checked={modeSite==='live'} onChange={()=> setModeSite('live')} />
                <span className="text-sm">Produção</span>
              </label>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Publishable Key (Teste)</Label>
              <Input value={pubTest} onChange={(e)=> setPubTest(e.target.value)} placeholder="pk_test_..." />
              <a className="text-[12px] text-primary underline" target="_blank" rel="noreferrer noopener" href="https://dashboard.stripe.com/test/apikeys">Abrir Stripe (Teste) — API keys</a>
            </div>
            <div className="space-y-1.5">
              <Label>Secret Key (Teste)</Label>
              <Input value={secTest} onChange={(e)=> setSecTest(e.target.value)} type="password" placeholder="sk_test_..." />
            </div>
            <div className="space-y-1.5">
              <Label>Publishable Key (Produção)</Label>
              <Input value={pubLive} onChange={(e)=> setPubLive(e.target.value)} placeholder="pk_live_..." />
              <a className="text-[12px] text-primary underline" target="_blank" rel="noreferrer noopener" href="https://dashboard.stripe.com/apikeys">Abrir Stripe (Produção) — API keys</a>
            </div>
            <div className="space-y-1.5">
              <Label>Secret Key (Produção)</Label>
              <Input value={secLive} onChange={(e)=> setSecLive(e.target.value)} type="password" placeholder="sk_live_..." />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Webhook Secret {hasWebhook && <span className="text-xs text-foreground/60">(já configurado)</span>}</Label>
            <Input value={webhookSecret} onChange={(e)=> setWebhookSecret(e.target.value)} type="password" placeholder="whsec_..." />
          </div>

          <Button type="submit" disabled={saving || loading}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </form>
      </CardContent>
      <SaveFeedbackModal open={saved} onClose={()=> setSaved(false)} title="Stripe" description="Credenciais salvas com sucesso." />
    </Card>
  )
}

